import React, {useState, useMemo} from "react"
import {Link} from "react-router-dom"
import moment from "moment"
import {useQuery} from "react-query"
import useAuth from "auth/useAuth"
import useShifts from "hooks/useShifts"
import useRescuers from "hooks/useRescuers"
import useShiftRescues from "hooks/useShiftRescues"
import "./ShiftRescueStats.css"


const AREA = "Kopaonik"


function getCurrentShiftStart() {
    const now = moment()
    let start = moment(now).day(6)
    if (start.isAfter(now)) start.subtract(7, 'days')
    if (start.isSame(now, 'day') && now.hour() < 18) start.subtract(7, 'days')
    return start.format('YYYY-MM-DD')
}


export default function ShiftRescueStats() {

    const {auth, API} = useAuth()
    const {getRescuers} = useShifts()
    const {getUser} = useRescuers()
    const [selectedWeek, setSelectedWeek] = useState(null)

    const {data: weeks} = useQuery("weeks", () => auth(API.shiftsWeeksGet).then(r => r.data), {select: data => data?.weeks ?? []})
    const currentStart = useMemo(() => getCurrentShiftStart(), [])

    const availableWeeks = useMemo(() =>
        weeks?.filter(w => moment(w).isSameOrBefore(currentStart, 'day')).reverse(),
        [weeks, currentStart]
    )

    const isPastShift = selectedWeek != null && selectedWeek !== currentStart

    const shiftRescuers = getRescuers(AREA)
    const {isLoading, isFetching, refetch, ownerCounts, participantCounts, start, end} = useShiftRescues(AREA, selectedWeek)

    if (!isPastShift && (!shiftRescuers || shiftRescuers.length === 0)) return null

    const makeRow = (username, given_name, family_name, inShift) => {
        const user = getUser(username)
        const name = [given_name, family_name].filter(Boolean).join(' ')
        const gssId = user?.gss_id
        const extra = [user?.nickname, gssId].filter(Boolean).join(' ')
        const idNum = parseInt(gssId, 10)
        return {
            username, name, extra, inShift, idNum: isNaN(idNum) ? Infinity : idNum,
            owner: ownerCounts.get(username) ?? 0,
            participant: participantCounts.get(username) ?? 0
        }
    }

    let rows
    if (isPastShift) {
        const allUsernames = new Set([...ownerCounts.keys(), ...participantCounts.keys()])
        rows = [...allUsernames].map(u => {
            const user = getUser(u)
            return makeRow(u, user?.given_name, user?.family_name, true)
        })
    } else {
        const shiftUsernames = new Set(shiftRescuers.map(r => r.username))
        const extraUsernames = new Set([...ownerCounts.keys()].filter(u => !shiftUsernames.has(u) && ownerCounts.get(u) > 0))
        rows = [
            ...shiftRescuers.map(r => makeRow(r.username, r.given_name, r.family_name, true)),
            ...[...extraUsernames].map(u => {
                const user = getUser(u)
                return makeRow(u, user?.given_name, user?.family_name, false)
            })
        ]
    }
    rows.sort((a, b) => b.owner - a.owner || b.participant - a.participant || a.idNum - b.idNum)

    const totalOwner = rows.reduce((s, r) => s + r.owner, 0)
    const totalParticipant = rows.reduce((s, r) => s + r.participant, 0)

    const currentDateRange = `${moment(currentStart).format("DD.MM")} – ${moment(currentStart).add(7, 'days').format("DD.MM")}`

    return <section id="shift-rescue-stats" className="container-fluid">
        <div className="stats-header halftone">
            <h5>{AREA}</h5>
            <button className="refresh-btn" onClick={refetch} disabled={isFetching} title="Osveži podatke">
                <i className={`fa-solid fa-arrows-rotate${isFetching ? ' fa-spin' : ''}`}/>
            </button>
            <select className="shift-select"
                value={selectedWeek ?? ""}
                onChange={e => setSelectedWeek(e.target.value || null)}>
                <option value="">Smena {currentDateRange}</option>
                {availableWeeks?.filter(w => w !== currentStart).map(w => {
                    const s = moment(w)
                    const e = moment(w).add(7, 'days')
                    return <option key={w} value={w}>Smena {s.format("DD.MM")} – {e.format("DD.MM")}</option>
                })}
            </select>
        </div>

        {isLoading ? <div id="spinner" className="center"/> : <table>
            <thead>
                <tr>
                    <th>Spasilac</th>
                    <th className="count" title="Vođa akcije">Vođa</th>
                    <th className="count" title="Učesnik">Uč.</th>
                </tr>
            </thead>
            <tbody>
                {rows.map(r => <tr key={r.username} className={r.inShift ? '' : 'not-in-shift'}>
                    <td><Link to={`/rescuer/${r.username}`}>{r.name}{r.extra && `, ${r.extra}`}</Link></td>
                    <td className="count">{r.owner || ""}</td>
                    <td className="count">{r.participant || ""}</td>
                </tr>)}
                <tr className="total-row">
                    <td>Ukupno</td>
                    <td className="count" colSpan="2">{totalOwner}</td>
                </tr>
            </tbody>
        </table>}
    </section>
}
